import Stack from "@mui/material/Stack"
import { useCallback, useEffect, useMemo, useState } from "react"

import BankCards from "@assets/svg_icons/bank_cards"
import BillingInformation from "@components/dialogs/billing_information"
import DeletePaymentCard from "@components/dialogs/delete_payment_card"
import TextActionForm from "@components/text_action_form"
import { PRIMARY } from "@constants/colors"
import { useCustomerPageContext } from "@contexts/customer"
import {
  accountGetCustomerBillingAddresses,
  accountGetCustomerPaymentCard
} from "@domain/customer/infos"
import useToggle from "@hooks/use_toggle"
import CreateIcon from "@mui/icons-material/Create"
import DeleteIcon from "@mui/icons-material/Delete"
import { CircularProgress } from "@mui/material"
import { CustomerBillingAddressesInput } from "__generated__/graphql"
import { useParams } from "react-router-dom"

export type BillingInformationData = Omit<
  CustomerBillingAddressesInput,
  "customer"
>

export type PaymentMethodSaveInformationData = {
  _id: string
  holderName: string
  cardNumber: string
  expirationDate: string
  cvv: string
}

export default function PaymentInformation() {
  const { translate } = useCustomerPageContext()
  const { id } = useParams()

  const [loadingBillingAddresses, setLoadingBillingAddresses] = useState(false)
  const [loadingPaymentCard, setLoadingPaymentCard] = useState(false)

  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethodSaveInformationData>()

  const [billingInformation, setBillingInformation] =
    useState<BillingInformationData>()

  const [
    isAddBillingInformationModalOpen,
    toggleIsAddBillingInformationModalOpen
  ] = useToggle()

  const [isDeletePaymentCardModalOpen, toggleIsDeletePaymentCardModalOpen] =
    useToggle()

  const fetchBillingAddresses = useCallback(async () => {
    if (!id) return false
    setLoadingBillingAddresses(true)
    try {
      const { isSuccess, data } = await accountGetCustomerBillingAddresses({
        customerId: id
      })
      if (isSuccess && data) {
        setBillingInformation(() => {
          const newData = data
            ? {
                accountingContactId: data.accountingContactId,
                address1: data.address1,
                address2: data.address2,
                city: data.city,
                country: data.country,
                isCompany: data.isCompany,
                legalCompanyName: data.legalCompanyName,
                postCode: data.postCode,
                province: data.province,
                state: data.state,
                taxNumber: data.taxNumber,
                taxOffice: data.taxOffice
              }
            : null
          return newData as BillingInformationData
        })
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
    setLoadingBillingAddresses(false)
  }, [id])

  const fetchCustomerPaymentCard = useCallback(async () => {
    if (!id) return false
    setLoadingPaymentCard(true)
    try {
      const { isSuccess, data } = await accountGetCustomerPaymentCard({
        customerId: id
      })
      if (isSuccess && data) {
        const defaultCard = data.cards?.find((c) => c?.default)
        setPaymentMethod(() => {
          const newData = defaultCard
            ? {
                holderName: defaultCard?.cardAlias,
                cardNumber: defaultCard?.lastFourDigits,
                _id: defaultCard?._id
              }
            : null
          return newData as PaymentMethodSaveInformationData
        })
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
    setLoadingPaymentCard(false)
  }, [id])

  useEffect(() => {
    fetchBillingAddresses()
    fetchCustomerPaymentCard()
  }, [])

  const billingText = useMemo(() => {
    if (billingInformation) {
      const { address1, address2, country, state, postCode, province, city } =
        billingInformation

      return `${address1} ${address2 || ""} ${country || ""} ${
        state || ""
      } ${province || ""} ${city || ""} ${postCode || ""}`
    } else {
      return translate("add_billing_information")
    }
  }, [billingInformation, translate])

  const paymentMethodText = useMemo(() => {
    if (paymentMethod) {
      const { holderName, cardNumber } = paymentMethod
      return `${holderName} ${cardNumber.toString().slice(-4)}`
    } else {
      return translate("add_payement_method")
    }
  }, [paymentMethod, translate])
  return (
    <Stack
      sx={{
        width: "100%"
      }}
      gap={1}
    >
      <TextActionForm
        label={translate("billing_information")}
        labelColor="black"
        textColor={billingInformation ? "black" : "gray"}
        text={loadingBillingAddresses ? "" : billingText}
        textSx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          height: "1.2em"
        }}
        ButtonRight={
          loadingBillingAddresses ? (
            <CircularProgress size={16} />
          ) : (
            <CreateIcon
              onClick={toggleIsAddBillingInformationModalOpen}
              sx={{
                color: "#868686",
                cursor: "pointer",
                transition: "color 0.3s",
                ":hover": { color: PRIMARY }
              }}
            />
          )
        }
      />
      <TextActionForm
        label={translate("parasut_id")}
        labelColor="black"
        textColor={billingInformation?.accountingContactId ? "black" : "gray"}
        text={
          loadingBillingAddresses
            ? ""
            : billingInformation?.accountingContactId ||
              translate("parasut_id_not_synced")
        }
        textSx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          height: "1.2em"
        }}
      />
      <TextActionForm
        label={translate("payment_method")}
        labelColor="black"
        text={loadingPaymentCard ? "" : paymentMethodText}
        textColor={paymentMethod ? "black" : "gray"}
        labelRight={<BankCards />}
        textSx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          height: "1.2em"
        }}
        ButtonRight={
          loadingPaymentCard ? (
            <CircularProgress size={16} />
          ) : (
            <DeleteIcon
              onClick={
                paymentMethod ? toggleIsDeletePaymentCardModalOpen : undefined
              }
              sx={{
                color: paymentMethod ? "#868686" : "#c0c0c0",
                cursor: paymentMethod ? "pointer" : "default",
                transition: "color 0.3s",
                ":hover": { color: paymentMethod ? PRIMARY : "#c0c0c0" }
              }}
            />
          )
        }
      />
      <BillingInformation
        billingInformation={billingInformation}
        setBillingInformation={setBillingInformation}
        isAddBillingInformationModalOpen={isAddBillingInformationModalOpen}
        toggleIsAddBillingInformationModalOpen={
          toggleIsAddBillingInformationModalOpen
        }
        customerId={id}
      />
      <DeletePaymentCard
        isDeletePaymentCardOpen={isDeletePaymentCardModalOpen}
        toggleIsDeletePaymentCardOpen={toggleIsDeletePaymentCardModalOpen}
        customerId={id}
        paymentCardId={paymentMethod?._id}
        setPaymentMethod={setPaymentMethod}
      />
    </Stack>
  )
}
